import { GameEngine } from './game-core.mjs';
import { ChallengeScene } from './ar-scene.js';
import { ChallengeTelemetry } from './telemetry.js';
import { AudioBus } from './audio.js';
import { VfxController } from './vfx.js';
import { captureCanvas, saveOrShareCapture } from './capture.js';
import {
  bindHud,
  show,
  setStatus,
  toast,
  updateMeters,
  showResults,
  setTargetBanner,
} from './ui.js';
import { targetFilePending } from './ar-scene.js';

const ui = bindHud();
const audio = new AudioBus();
const params = new URLSearchParams(location.search);
const forcedMode = params.get('mode'); // 'test' | 'ar' | null
/** Hook herramienta: ?character=/assets/guardian.glb */
const characterUrl = params.get('character') || null;

let engine = null;
let scene = null;
let telemetry = null;
let vfx = null;
let tickRaf = 0;
let lastTs = 0;
let tapCount = 0;
let lastCapture = null;
let finishing = false;
let targetPresent = false;

async function probeTargetFile() {
  const url = targetFilePending();
  try {
    const probe = await fetch(url, { method: 'GET', cache: 'no-store' });
    const ct = probe.headers.get('content-type') || '';
    // Vite con appType mpa no debería devolver HTML; por si acaso.
    const ok = probe.ok && !ct.includes('text/html');
    if (ok) await probe.arrayBuffer();
    targetPresent = ok;
    setTargetBanner(ui, {
      present: ok,
      detail: ok
        ? `Target listo (${url}). Imprime challenge-target.png (mate, A5/A4) y usa HTTPS/móvil.`
        : `Fallback activo: no hay ${url}. Cámara AR no anclará. Genera con npm run train:target o usa modo prueba.`,
    });
    if (!ok) ui.btnAr.title = 'Sin .zpt: cámara sin ancla (fallback)';
    else ui.btnAr.title = 'Image Tracking con challenge-target.zpt';
    return ok;
  } catch (err) {
    targetPresent = false;
    setTargetBanner(ui, {
      present: false,
      detail: `Fallback: no se pudo leer el .zpt (${err.message}). Usa modo prueba.`,
    });
    return false;
  }
}

function readConfig() {
  return {
    apiBase: ui.apiBase.value.trim() || 'https://experience.untitledps.com',
    campaign: ui.campaign.value.trim() || 'demo-xr',
  };
}

async function bootTelemetry() {
  telemetry = new ChallengeTelemetry(readConfig());
  try {
    const session = await telemetry.start();
    setStatus(ui.bootStatus, `Sesión API ok (${session.sessionId}).`, 'ok');
    return true;
  } catch (err) {
    setStatus(
      ui.bootStatus,
      `API no disponible: ${err.message}. Puedes jugar; telemetría desactivada.`,
      'warn',
    );
    telemetry = null;
    return false;
  }
}

async function startExperience(mode) {
  await audio.unlock();
  show(ui.boot, false);
  show(ui.results, false);
  show(ui.hud, true);
  ui.hint.textContent =
    mode === 'test'
      ? 'Modo prueba: 20 s · meta 50 orbes. El Guardián celebra y muestra tu marcador.'
      : 'Apunta al target. Tracking → 20 s · meta 50 orbes. Toca orbes (no al Guardián).';

  vfx = new VfxController(ui.vfxLayer);
  // Timer 20s = límite principal; meta 50 recolectadas clasifica victoria al cerrar el reloj.
  engine = new GameEngine({ durationMs: 20_000, targetScore: 50, maxHitsPerSecond: 10 });
  tapCount = 0;
  lastCapture = null;
  finishing = false;
  show(ui.capturePreview, false);

  scene?.dispose();
  scene = new ChallengeScene({
    canvas: ui.canvas,
    mode,
    characterUrl,
    getScoreSnapshot: () => (engine ? engine.snapshot() : null),
    onTracking: async (acquired) => {
      if (!engine) return;
      if (acquired) {
        ui.hint.textContent =
          '¡Tracking! 20 s — toca orbes. El Guardián celebra cada uno y lleva tu X/50.';
        if (engine.snapshot().status === 'ready') {
          engine.start();
          audio.start();
          await telemetry?.gameStarted();
          updateMeters(ui, engine.snapshot());
        } else if (engine.snapshot().status === 'paused') {
          engine.resume();
        }
      } else {
        ui.hint.textContent = 'Tracking perdido — reencuadra el target.';
        if (engine.snapshot().status === 'playing') engine.pause();
      }
      await telemetry?.tracking(acquired);
    },
    onCollect: (_world, screen) => {
      if (!engine || engine.snapshot().status !== 'playing') return false;
      const ok = engine.hit();
      if (!ok) return false;
      tapCount += 1;
      audio.collect();
      vfx.burst(screen.x, screen.y);
      vfx.flashHud();
      const snap = engine.snapshot();
      updateMeters(ui, snap);
      if (snap.combo >= 3) {
        ui.hint.textContent = `Combo ×${snap.combo} — el Guardián se anima. Sigue a ${snap.score}/50.`;
      }
      void telemetry?.interaction(tapCount);
      if (engine.snapshot().status === 'finished') finishRound();
      return true;
    },
  });

  const info = await scene.init();
  if (info.targetMissing) {
    targetPresent = false;
    setTargetBanner(ui, { present: false });
    ui.hint.textContent =
      'Fallback: falta challenge-target.zpt — cámara activa sin ancla. Usa modo prueba o npm run train:target.';
    toast('Fallback: .zpt ausente');
  } else if (mode === 'ar') {
    targetPresent = true;
    setTargetBanner(ui, { present: true });
  }

  if (mode === 'test') {
    engine.start();
    audio.start();
    await telemetry?.gameStarted();
    updateMeters(ui, engine.snapshot());
  }

  lastTs = performance.now();
  cancelAnimationFrame(tickRaf);
  tickRaf = requestAnimationFrame(gameTick);
}

function gameTick(ts) {
  tickRaf = requestAnimationFrame(gameTick);
  if (!engine) return;
  const raw = ts - lastTs;
  lastTs = ts;
  // rAF puede entregar dt negativo/NaN tras background o primer frame.
  const dt = Number.isFinite(raw) && raw > 0 ? Math.min(raw, 100) : 0;
  const snap = engine.tick(dt);
  updateMeters(ui, snap);
  if (snap.status === 'finished') finishRound();
}

async function finishRound() {
  if (finishing || !engine) return;
  finishing = true;
  cancelAnimationFrame(tickRaf);
  const snap = engine.snapshot();
  if (snap.result === 'won') audio.win();
  else audio.lose();

  await telemetry?.gameFinished({
    score: snap.score,
    result: snap.result,
    durationMs: snap.elapsedMs,
  });

  try {
    lastCapture = await captureCanvas(ui.canvas);
    ui.capturePreview.src = lastCapture.dataUrl;
    show(ui.capturePreview, true);
  } catch (err) {
    console.warn('captura', err);
  }

  showResults(ui, snap);
  setStatus(ui.resultsStatus, 'Partida registrada (telemetría no autoritativa).', 'ok');
}

async function onCaptureShare() {
  if (!lastCapture) {
    try {
      lastCapture = await captureCanvas(ui.canvas);
      ui.capturePreview.src = lastCapture.dataUrl;
      show(ui.capturePreview, true);
    } catch (err) {
      setStatus(ui.resultsStatus, `No se pudo capturar: ${err.message}`, 'err');
      return;
    }
  }
  try {
    const result = await saveOrShareCapture(lastCapture);
    await telemetry?.share();
    setStatus(ui.resultsStatus, `Captura: ${result.method}`, 'ok');
    toast(result.method === 'share' ? 'Compartido' : 'Descargado');
  } catch (err) {
    setStatus(ui.resultsStatus, `Share cancelado o fallido: ${err.message}`, 'warn');
  }
}

function goHome() {
  cancelAnimationFrame(tickRaf);
  scene?.dispose();
  scene = null;
  engine = null;
  telemetry?.clear();
  telemetry = null;
  show(ui.results, false);
  show(ui.hud, false);
  show(ui.boot, true);
  setStatus(ui.bootStatus, '');
}

/** Quita overlays nativos de Zappar al volver al boot (p. ej. permiso denegado). */
function clearZapparPermissionDom() {
  document
    .querySelectorAll('.zappar-permission-request, .zappar-permission-denied')
    .forEach((el) => el.remove());
}

/**
 * Bloqueo de CTAs con timeout: si audio/cámara se cuelga, el botón no queda
 * disabled para siempre (repro histórico en ?mode=ar).
 */
const BOOT_LOCK_TIMEOUT_MS = 25_000;

async function runFromBoot(mode) {
  ui.btnAr.disabled = true;
  ui.btnTest.disabled = true;
  ui.btnAr.classList.remove('needs-tap');
  try {
    await bootTelemetry();
    await Promise.race([
      startExperience(mode),
      new Promise((_, reject) => {
        setTimeout(
          () =>
            reject(
              new Error(
                'El arranque tardó demasiado. Toca de nuevo «Jugar con cámara AR» (hace falta un gesto real para audio/cámara).',
              ),
            ),
          BOOT_LOCK_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (err) {
    clearZapparPermissionDom();
    setStatus(ui.bootStatus, err.message || String(err), 'err');
    show(ui.boot, true);
    show(ui.hud, false);
  } finally {
    ui.btnAr.disabled = false;
    ui.btnTest.disabled = false;
  }
}

ui.btnAr.addEventListener('click', () => {
  void runFromBoot('ar');
});

ui.btnTest.addEventListener('click', () => {
  void runFromBoot('test');
});

ui.btnCapture.addEventListener('click', onCaptureShare);
ui.btnReplay.addEventListener('click', () => {
  const mode = scene?.mode || (forcedMode === 'ar' ? 'ar' : 'test');
  startExperience(mode);
});
ui.btnHome.addEventListener('click', goHome);

// Probe .zpt al boot; deep-links tras el banner.
// Importante: NO auto-click en ?mode=ar — el click programático no aporta
// transient activation → AudioContext.resume() y getUserMedia pueden colgarse
// con el botón disabled/opaco (falla reportada en challenge.untitledps.com/?mode=ar).
queueMicrotask(async () => {
  await probeTargetFile();
  if (forcedMode === 'test') {
    ui.btnTest.click();
    return;
  }
  if (forcedMode === 'ar') {
    setStatus(
      ui.bootStatus,
      'Listo para AR — toca «Jugar con cámara AR» (el navegador exige un gesto real para cámara y audio).',
      'ok',
    );
    ui.btnAr.classList.add('needs-tap');
    ui.btnAr.focus({ preventScroll: true });
  }
});
